<script setup>
import { ref, onMounted, reactive } from 'vue';
import { callAPI, DRUPAL_API_ROUTE } from "../service/DrupalService";
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useI18n } from 'vue-i18n'

const { t } = useI18n();

const schema = yup.object({
  QRCodeName: yup.string().required()
});

const { defineField, handleSubmit, resetForm, errors } = useForm({
  validationSchema: schema,
});

const [QRCodeName] = defineField('QRCodeName');

const props = defineProps(['modelValue', 'qrcodeTemplateUUID', 'qrcodeTemplateCreate', 'shortlink', 'shortlinkUUID']);
const emit = defineEmits(['update:modelValue', 'closeDialog', 'reload']);

const user = ref();
const qrcodesTemplatesList = ref();
const QRCodePreviewLoading = ref(false);
const qrcodesTemplatesCreateLoading = ref(false);
const QRCodeDownloadLoading = ref(false);
const crsf = ref();

const initialState = {
  QRCodeAccordeon: ['0'],
  QRCodeToggle: false,
  QRCodeColor: '000',
  QRCodeLogo: false,
  QRCodeLogoSRC: '',
  QRCodeShape: "0",
  QRCodeEyes: "0",
  QRCodePOTL: '000',
  QRCodePITL: '000',
  QRCodePOTR: '000',
  QRCodePITR: '000',
  QRCodePOBL: '000',
  QRCodePIBL: '000',
  QRCodeFrame: "0",
  QRCodeFrameColor: '000',
  QRCodeFrameTitle: '',
  QRCodeFrameTextColor: false,
  QRCodeTemplateUUID: '',
  QRCodeName: '',
  QRCodesTemplateSelected: '',
  QRCodeFileUploadURL: DRUPAL_API_ROUTE.IMAGE_UPLOAD,
  QRCodeFormatPNG: true,
  QRCodeFormatSVG: true
};

const qrcodesItems = [
  {
    label: 'PNG',
    icon: 'pi pi-download',
    command: () => {
      QRCodeDownload('png');
    },
  },
  {
    label: 'SVG',
    icon: 'pi pi-download',
    command: () => {
      QRCodeDownload('svg');
    },
  },
];

let formData = reactive({ ...initialState });

async function onBeforeSend(request) {
  request.xhr.setRequestHeader('X-CSRF-Token', crsf.value);
}

const onUpload = async (event) => {
  var response = JSON.parse(event.xhr.response);
  if (response.base64) {
    formData.QRCodeLogo = response.base64;
    formData.QRCodeLogoSRC = 'data:image/png;base64,' + response.base64;
    await generateQRCode();
  }
};

const clearCallback = async () => {
  formData.QRCodeLogo = false;
  formData.QRCodeLogoSRC = '';
  await generateQRCode();
}

const getQRCodesTemplates = async () => {
  try {
    var response = await callAPI(DRUPAL_API_ROUTE.QRCODES_TEMPLATES);
    qrcodesTemplatesList.value = response.data;
  } catch (err) {
    console.log(err);
  }
};

const loadTemplate = async (uuid) => {
  if (!uuid) return await resetFormData();

  QRCodePreviewLoading.value = true;

  try {
    var response = await callAPI(DRUPAL_API_ROUTE.QRCODES_TEMPLATE, { uuid: uuid });
    var template = response.data;

  } catch (err) {
    console.log(err);
  }

  var data = {
    'uuid': false,
    'template': response.data.id,
    'shortlink': props.shortlink || 'https://lc.cx',
    'action': 'generateQRCodeFromTemplate',
    'preview': true,
  }

  try {
    var response = await callAPI(DRUPAL_API_ROUTE.SHORTLINK_QRCODE_CREATE, {}, data);
    var qrcode = response.data;

    formData.QRCodeBase64 = qrcode;

    formData.QRCodeName = template.name;
    QRCodeName.value = template.name;
    formData.QRCodeFrameTitle = template.title;
    formData.QRCodeColor = template.color;
    formData.QRCodeLogo = template.logo;
    formData.QRCodeLogoSRC = 'data:image/png;base64,' + template.logo;
    formData.QRCodeShape = template.shape.toString();
    formData.QRCodeEyes = template.eyes.toString();
    formData.QRCodePOTL = template.po_tl || '000';
    formData.QRCodePITL = template.pi_tl || '000'
    formData.QRCodePOTR = template.po_tr || '000';
    formData.QRCodePITR = template.pi_tr || '000'
    formData.QRCodePOBL = template.po_bl || '000'
    formData.QRCodePIBL = template.pi_bl || '000';
    formData.QRCodeFrame = template.frame.toString();
    formData.QRCodeFrameColor = template.frame_color;
    formData.QRCodeFrameTextColor = template.frame_color;
    formData.QRCodeFormatPNG = true;
    formData.QRCodeFormatSVG = template.svg;

    emit('update:modelValue', formData);

  } catch (err) {
    console.log(err);
  }

  QRCodePreviewLoading.value = false;
}

const generateQRCode = async () => {
  var data = {
    'uuid': false,
    'template': false,
    'svg': false,
    'shortlink': props.shortlink || 'https://lc.cx',
    'action': 'generateOneTimeCustomQRCode',
    'preview': true,
    'title': formData.QRCodeFrameTitle || false,
    'color': formatQRCodeHexString(formData.QRCodeColor) || false,
    'logo': formData.QRCodeLogo || false,
    'shape': formData.QRCodeShape || false,
    'eyes': formData.QRCodeEyes || false,
    'po_tl': formatQRCodeHexString(formData.QRCodePOTL) || false,
    'pi_tl': formatQRCodeHexString(formData.QRCodePITL) || false,
    'po_tr': formatQRCodeHexString(formData.QRCodePOTR) || false,
    'pi_tr': formatQRCodeHexString(formData.QRCodePITR) || false,
    'po_bl': formatQRCodeHexString(formData.QRCodePOBL) || false,
    'pi_bl': formatQRCodeHexString(formData.QRCodePIBL) || false,
    'frame': formData.QRCodeFrame || false,
    'frame_color': formatQRCodeHexString(formData.QRCodeFrameColor) || false,
    'frame_text_color': formData.QRCodeFrameTextColor || false,
  }

  try {
    var response = await callAPI(DRUPAL_API_ROUTE.SHORTLINK_QRCODE_CREATE, {}, data);
    var qrcode = response.data;
    formData.QRCodeBase64 = qrcode;
  } catch (err) {
    console.log(err);
  }

  var response = await callAPI(DRUPAL_API_ROUTE.CRSF_TOKEN);
  crsf.value = response.data;
}

const onSubmitqrcodesTemplate = handleSubmit((values) => {
  formData.QRCodeName = values.QRCodeName;
  if (props.qrcodeTemplateUUID) {
    qrcodesTemplateEdit();
  } else {
    qrcodesTemplateCreate();
  }
});

const qrcodesTemplateCreate = async () => {
  qrcodesTemplatesCreateLoading.value = true;

  var data = {
    'name': formData.QRCodeName,
    'svg': formData.QRCodeFormatSVG,
    'title': formData.QRCodeFrameTitle || false,
    'color': formatQRCodeHexString(formData.QRCodeColor) || false,
    'logo': formData.QRCodeLogo || false,
    'shape': formData.QRCodeShape || false,
    'eyes': formData.QRCodeEyes || false,
    'po_tl': formatQRCodeHexString(formData.QRCodePOTL) || false,
    'pi_tl': formatQRCodeHexString(formData.QRCodePITL) || false,
    'po_tr': formatQRCodeHexString(formData.QRCodePOTR) || false,
    'pi_tr': formatQRCodeHexString(formData.QRCodePITR) || false,
    'po_bl': formatQRCodeHexString(formData.QRCodePOBL) || false,
    'pi_bl': formatQRCodeHexString(formData.QRCodePIBL) || false,
    'frame': formData.QRCodeFrame || false,
    'frame_color': formatQRCodeHexString(formData.QRCodeFrameColor) || false,
    'frame_text_color': formData.QRCodeFrameTextColor || false,
  }

  data.journal = user.value.permissions.save_log;

  try {
    await callAPI(DRUPAL_API_ROUTE.QRCODE_TEMPLATE_ADD, {}, data);
  } catch (err) {
    console.log(err);
  } finally {
    emit('closeDialog');
    emit('reload');

    qrcodesTemplatesCreateLoading.value = false;
  }
}

const qrcodesTemplateEdit = async () => {
  qrcodesTemplatesCreateLoading.value = true;

  var data = {
    'name': formData.QRCodeName,
    'svg': formData.QRCodeFormatSVG,
    'title': formData.QRCodeFrameTitle || false,
    'color': formatQRCodeHexString(formData.QRCodeColor) || false,
    'logo': formData.QRCodeLogo || false,
    'shape': formData.QRCodeShape || false,
    'eyes': formData.QRCodeEyes || false,
    'po_tl': formatQRCodeHexString(formData.QRCodePOTL) || false,
    'pi_tl': formatQRCodeHexString(formData.QRCodePITL) || false,
    'po_tr': formatQRCodeHexString(formData.QRCodePOTR) || false,
    'pi_tr': formatQRCodeHexString(formData.QRCodePITR) || false,
    'po_bl': formatQRCodeHexString(formData.QRCodePOBL) || false,
    'pi_bl': formatQRCodeHexString(formData.QRCodePIBL) || false,
    'frame': formData.QRCodeFrame || false,
    'frame_color': formatQRCodeHexString(formData.QRCodeFrameColor) || false,
    'frame_text_color': formatQRCodeHexString(formData.QRCodeFrameTextColor) || false,
  }

  data.journal = user.value.permissions.save_log;

  try {
    await callAPI(DRUPAL_API_ROUTE.QRCODE_TEMPLATE_EDIT, { uuid: props.qrcodeTemplateUUID }, data);
  } catch (err) {
    console.log(err);
  } finally {
    emit('closeDialog');
    emit('reload');

    qrcodesTemplatesCreateLoading.value = false;
  }
}

const QRCodeDownload = async (format) => {
  QRCodeDownloadLoading.value = true;

  var data = {
    'name': formData.QRCodeName,
    'format': format,
    'title': formData.QRCodeFrameTitle || false,
    'color': formatQRCodeHexString(formData.QRCodeColor) || false,
    'logo': formData.QRCodeLogo || false,
    'shape': formData.QRCodeShape || false,
    'eyes': formData.QRCodeEyes || false,
    'po_tl': formatQRCodeHexString(formData.QRCodePOTL) || false,
    'pi_tl': formatQRCodeHexString(formData.QRCodePITL) || false,
    'po_tr': formatQRCodeHexString(formData.QRCodePOTR) || false,
    'pi_tr': formatQRCodeHexString(formData.QRCodePITR) || false,
    'po_bl': formatQRCodeHexString(formData.QRCodePOBL) || false,
    'pi_bl': formatQRCodeHexString(formData.QRCodePIBL) || false,
    'frame': formData.QRCodeFrame || false,
    'frame_color': formatQRCodeHexString(formData.QRCodeFrameColor) || false,
    'frame_text_color': formData.QRCodeFrameTextColor || false,
  }

  var request = await callAPI(DRUPAL_API_ROUTE.SHORTLINK_QRCODE_UUID, { uuid: props.shortlinkUUID }, data);
  var url = request.data;

  window.open(url, '_blank');

  QRCodeDownloadLoading.value = false;
}

const handleQRCode = async () => {
  formData.QRCodesTemplateSelected = '';
  emit('update:modelValue', formData);
  await generateQRCode();
}

const resetFormData = async () => {
  resetForm();
  if (!props.qrcodeTemplateUUID) {
    Object.assign(formData, initialState);
    await generateQRCode();
  } else {
    loadTemplate(props.qrcodeTemplateUUID);
  }
}

function formatQRCodeHexString(input) {
  var color = input.startsWith('#') ? input : `#${input}`;
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color) ? color : '#000';
}

onMounted(() => {
  user.value = JSON.parse(localStorage.getItem('user'));
  if (!user.value.permissions.generate_svg_qrcode) qrcodesItems[1].disabled = true;
  if (props.qrcodeTemplateUUID) {
    loadTemplate(props.qrcodeTemplateUUID);
  } else {
    generateQRCode();
  }
  if (!props.qrcodeTemplateUUID && !props.qrcodeTemplateCreate) getQRCodesTemplates();
});

</script>
<template>
  <div class="flex flex-col gap-2 space-y-4">
    <div class="flex justify-center">
      <div v-if="QRCodePreviewLoading">
        <Skeleton size="6rem"></Skeleton>
      </div>
      <div v-else>
        <Image alt="Image" preview>
          <template #previewicon>
            <i class="pi pi-search"></i>
          </template>
          <template #image>
            <img class="max-w-64" :src="formData.QRCodeBase64" />
          </template>
          <template #original="slotProps">
            <img :src="formData.QRCodeBase64" alt="preview" :style="slotProps.style" @click="slotProps.onClick" />
          </template>
        </Image>
      </div>
    </div>
    <div v-if="props.qrcodeTemplateUUID || props.qrcodeTemplateCreate" class="flex flex-col w-1/2 gap-2 justify-center">
      <label for="QRCodeName">{{ t('qrcodes.name') }}</label>
      <InputText type="search" v-model="QRCodeName" :class="{ 'p-invalid': errors.QRCodeName }" />
      <Message v-if="errors.QRCodeName" severity="error" size="small" variant="simple">{{
        errors.QRCodeName }}
      </Message>
    </div>
    <Accordion :value="formData.QRCodeAccordeon" multiple>
      <AccordionPanel value="0">
        <AccordionHeader>{{ t('qrcodes.style') }}</AccordionHeader>
        <AccordionContent>
          <div class="flex flex-wrap gap-10">
            <div class="flex items-center">
              <RadioButton v-model="formData.QRCodeShape" @change="handleQRCode" value="0" />
              <img class="lazy size-16 ml-5" src="@/assets/images/qrcodes/shape_classic.png">
            </div>
            <div class="flex items-center">
              <RadioButton v-model="formData.QRCodeShape" @change="handleQRCode" value="1" />
              <img class="lazy size-16 ml-5" src="@/assets/images/qrcodes/shape_circle.png">
            </div>
            <div class="flex items-center">
              <RadioButton v-model="formData.QRCodeShape" @change="handleQRCode" value="2" />
              <img class="lazy size-16 ml-5" src="@/assets/images/qrcodes/shape_diamond.png">
            </div>
            <div class="flex items-center">
              <RadioButton v-model="formData.QRCodeShape" @change="handleQRCode" value="3" />
              <img class="lazy size-16 ml-5" src="@/assets/images/qrcodes/shape_dot_matrix.png">
            </div>
            <div class="flex items-center">
              <RadioButton v-model="formData.QRCodeShape" @change="handleQRCode" value="4" />
              <img class="lazy size-16 ml-5" src="@/assets/images/qrcodes/shape_glue.png">
            </div>
          </div>
        </AccordionContent>
      </AccordionPanel>
      <AccordionPanel value="1">
        <AccordionHeader>{{ t('qrcodes.sides') }}</AccordionHeader>
        <AccordionContent>
          <div class="flex flex-wrap gap-10">
            <div class="flex items-center">
              <RadioButton v-model="formData.QRCodeEyes" @change="handleQRCode" value="0" />
              <img class="lazy size-16 ml-5" src="@/assets/images/qrcodes/eyes_square.png">
            </div>
            <div class="flex items-center">
              <RadioButton v-model="formData.QRCodeEyes" @change="handleQRCode" value="1" />
              <img class="lazy size-16 ml-5" src="@/assets/images/qrcodes/eyes_semi_circle.png">
            </div>
            <div class="flex items-center">
              <RadioButton v-model="formData.QRCodeEyes" @change="handleQRCode" value="2" />
              <img class="lazy size-16 ml-5" src="@/assets/images/qrcodes/eyes_circle.png">
            </div>
            <div class="flex items-center">
              <RadioButton v-model="formData.QRCodeEyes" @change="handleQRCode" value="3" />
              <img class="lazy size-16 ml-5" src="@/assets/images/qrcodes/eyes_circles.png">
            </div>
            <div class="flex items-center">
              <RadioButton v-model="formData.QRCodeEyes" @change="handleQRCode" value="4" />
              <img class="lazy size-16 ml-5" src="@/assets/images/qrcodes/eyes_mixed.png">
            </div>
          </div>
        </AccordionContent>
      </AccordionPanel>
      <AccordionPanel value="2">
        <AccordionHeader>{{ t('qrcodes.colors.title') }}</AccordionHeader>
        <AccordionContent>
          <div class="flex items-center mt-3 mb-10 space-x-8 text-sm">
            <div class="flex flex-col flex-1 space-y-2">
              <label for="QRCodeColor"> {{ t('qrcodes.colors.QRCodeColor') }}</label>
              <div class="flex items-center gap-1 w-2/3">
                <InputText size="small" type="text" v-model="formData.QRCodeColor" @change="handleQRCode" />
                <ColorPicker v-model="formData.QRCodeColor" @hide="handleQRCode" />
              </div>
            </div>
            <div class="flex flex-col flex-1 space-y-2">
              <label for="QRCodePOTL"> {{ t('qrcodes.colors.QRCodePOTL') }}</label>
              <div class="flex items-center gap-1 w-2/3">
                <InputText size="small" type="text" v-model="formData.QRCodePOTL" @change="handleQRCode" />
                <ColorPicker v-model="formData.QRCodePOTL" @hide="handleQRCode" />
              </div>
            </div>
          </div>
          <div class="flex items-center mb-10 space-x-8 text-sm">
            <div class="flex flex-col flex-1 space-y-2">
              <label for="QRCodePITL"> {{ t('qrcodes.colors.QRCodePITL') }}</label>
              <div class="flex items-center gap-1 w-2/3">
                <InputText size="small" type="text" v-model="formData.QRCodePITL" @change="handleQRCode" />
                <ColorPicker v-model="formData.QRCodePITL" @hide="handleQRCode" />
              </div>
            </div>
            <div class="flex flex-col flex-1 space-y-2">
              <label for="QRCodePOTR"> {{ t('qrcodes.colors.QRCodePOTR') }}</label>
              <div class="flex items-center gap-1 w-2/3">
                <InputText size="small" type="text" v-model="formData.QRCodePOTR" @change="handleQRCode" />
                <ColorPicker v-model="formData.QRCodePOTR" @hide="handleQRCode" />
              </div>
            </div>
          </div>
          <div class="flex items-center space-x-8 mb-10 text-sm">
            <div class="flex flex-col flex-1 space-y-2">
              <label for="QRCodePITR"> {{ t('qrcodes.colors.QRCodePITR') }}</label>
              <div class="flex items-center gap-1 w-2/3">
                <InputText size="small" type="text" v-model="formData.QRCodePITR" @change="handleQRCode" />
                <ColorPicker v-model="formData.QRCodePITR" @hide="handleQRCode" />
              </div>
            </div>
            <div class="flex flex-col flex-1 space-y-2">
              <label for="QRCodePOBL"> {{ t('qrcodes.colors.QRCodePOBL') }}</label>
              <div class="flex items-center gap-1 w-2/3">
                <InputText size="small" type="text" v-model="formData.QRCodePOBL" @change="handleQRCode" />
                <ColorPicker v-model="formData.QRCodePOBL" @hide="handleQRCode" />
              </div>
            </div>
          </div>
          <div class="flex items-center space-x-8 text-sm">
            <div class="flex flex-col flex-1 space-y-2">
              <label for="QRCodePIBL"> {{ t('qrcodes.colors.QRCodePIBL') }}</label>
              <div class="flex items-center gap-1 w-2/3">
                <InputText size="small" type="text" v-model="formData.QRCodePIBL" @change="handleQRCode" />
                <ColorPicker v-model="formData.QRCodePIBL" @hide="handleQRCode" />
              </div>
            </div>
            <div class="flex flex-col flex-1">
              <span class="invisible">Placeholder</span>
            </div>
          </div>
        </AccordionContent>
      </AccordionPanel>
      <AccordionPanel value="3">
        <AccordionHeader>{{ t('qrcodes.frames.title') }}</AccordionHeader>
        <AccordionContent>
          <div class="flex flex-wrap gap-10">
            <div class="flex items-center">
              <RadioButton v-model="formData.QRCodeFrame" @change="handleQRCode" value="0" />
              <label for="ingredient1" class="ml-2">{{ t('qrcodes.frames.none') }}</label>
            </div>
            <div class="flex items-center">
              <RadioButton v-model="formData.QRCodeFrame" @change="handleQRCode" value="1" />
              <img class="lazy size-16 ml-5" src="@/assets/images/qrcodes/around_1.png">
            </div>
            <div class="flex items-center">
              <RadioButton v-model="formData.QRCodeFrame" @change="handleQRCode" value="2" />
              <img class="lazy size-16 ml-5" src="@/assets/images/qrcodes/around_2.png">
            </div>
            <div class="flex items-center">
              <RadioButton v-model="formData.QRCodeFrame" @change="handleQRCode" value="3" />
              <img class="lazy size-16 ml-5" src="@/assets/images/qrcodes/around_3.png">
            </div>
            <div v-if="formData.QRCodeFrame != 0" class="flex flex-row space-x-10">
              <div class="flex flex-col gap-2">
                <label for="username">
                  {{ t('qrcodes.frames.title2') }}</label>
                <InputText type="search" v-model="formData.QRCodeFrameTitle" @input="handleQRCode" />
              </div>
              <div class="flex flex-col space-y-2">
                <div class="flex flex-col flex-1 space-y-2">
                  <label for="QRCodeFrameColor"> {{ t('qrcodes.frames.color') }}</label>
                  <div class="flex items-center gap-1 w-2/3">
                    <InputText size="small" type="text" v-model="formData.QRCodeFrameColor" @change="handleQRCode" />
                    <ColorPicker v-model="formData.QRCodeFrameColor" @hide="handleQRCode" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AccordionContent>
      </AccordionPanel>
      <AccordionPanel value="4">
        <AccordionHeader>{{ t('qrcodes.logo') }}</AccordionHeader>
        <AccordionContent>
          <div class="flex items-center">
            <div v-if="!formData.QRCodeLogo">
              <FileUpload :withCredentials="true" mode="basic" name="file" :url="formData.QRCodeFileUploadURL"
                accept="image/*" :maxFileSize="1000000" @before-send="onBeforeSend" @upload="onUpload" :auto="true"
                :chooseLabel="t('common.labels.browse')" />
            </div>
            <div v-else class="flex space-x-4">
              <img class="max-w-32" :src="formData.QRCodeLogoSRC" />
              <Button @click="clearCallback()" icon="pi pi-times" rounded outlined severity="danger"></Button>
            </div>
          </div>
        </AccordionContent>
      </AccordionPanel>
      <AccordionPanel v-if="!props.qrcodeTemplateUUID && !props.qrcodeTemplateCreate" value="5">
        <AccordionHeader>{{ t('qrcodes.templates') }}</AccordionHeader>
        <AccordionContent>
          <div class="flex items-center">
            <Select @change="loadTemplate(formData.QRCodesTemplateSelected?.id)"
              v-model="formData.QRCodesTemplateSelected" :options="qrcodesTemplatesList"
              :placeholder="t('common.placeholders.select')" optionLabel="name" showClear />
          </div>
        </AccordionContent>
      </AccordionPanel>
      <AccordionPanel v-if="props.qrcodeTemplateUUID || props.qrcodeTemplateCreate" value="5">
        <AccordionHeader>{{ t('qrcodes.formats') }}</AccordionHeader>
        <AccordionContent>
          <div class="flex flex-wrap gap-4">
            <div class="flex items-center gap-2">
              <Checkbox v-model="formData.QRCodeFormatPNG" inputId="png" name="png" binary :disabled="true" />
              <label for="png">PNG</label>
            </div>
            <div class="flex items-center gap-2">
              <Checkbox v-model="formData.QRCodeFormatSVG" inputId="svg" name="svg" binary
                :disabled="props.qrcodeTemplateCreate" />
              <label for="svg">SVG</label>
            </div>
          </div>
        </AccordionContent>
      </AccordionPanel>
    </Accordion>
    <div v-if="props.qrcodeTemplateUUID" class="flex justify-end gap-2 mt-5">
      <Button type="button" :label="t('common.labels.cancel')" severity="secondary"
        @click="emit('closeDialog')"></Button>
      <Button type="button" :label="t('common.labels.save')" @click="onSubmitqrcodesTemplate"
        :loading="qrcodesTemplatesCreateLoading"></Button>
    </div>
    <div v-if="props.qrcodeTemplateCreate" class="flex justify-end gap-2 mt-5">
      <Button type="button" :label="t('common.labels.cancel')" severity="secondary"
        @click="emit('closeDialog');"></Button>
      <Button type="button" :label="t('common.labels.save')" @click="onSubmitqrcodesTemplate"
        :loading="qrcodesTemplatesCreateLoading"></Button>
    </div>
    <div class="flex justify-center gap-2" v-if="props.shortlink">
      <SplitButton size="large" :label="t('common.labels.download')" @click="QRCodeDownload('png')"
        :model="qrcodesItems" :disabled="QRCodeDownloadLoading" />
    </div>
  </div>
</template>